import { type RefObject, useEffect, useLayoutEffect } from 'react';

const alignRowHeights = (left: HTMLElement, right: HTMLElement) => {
  const leftTable = left.querySelector('table');
  const rightTable = right.querySelector('table');
  if (!leftTable || !rightTable) return;

  const leftRows = Array.from(leftTable.rows);
  const rightRows = Array.from(rightTable.rows);
  if (leftRows.length !== rightRows.length) return;

  const pairs = leftRows.map((row, index) => [row, rightRows[index]] as const);
  for (const [leftRow, rightRow] of pairs) {
    leftRow.style.height = '';
    rightRow.style.height = '';
  }
  const heights = pairs.map(([leftRow, rightRow]) =>
    Math.max(
      leftRow.getBoundingClientRect().height,
      rightRow.getBoundingClientRect().height,
    ),
  );
  pairs.forEach(([leftRow, rightRow], index) => {
    leftRow.style.height = `${heights[index]}px`;
    rightRow.style.height = `${heights[index]}px`;
  });
};

// The frozen and scrolling columns are separate tables, so their rows drift apart.
export const useAlignedRowHeights = (
  leftRef: RefObject<HTMLElement | null>,
  rightRef: RefObject<HTMLElement | null>,
) => {
  useLayoutEffect(() => {
    if (leftRef.current && rightRef.current) {
      alignRowHeights(leftRef.current, rightRef.current);
    }
  });

  useEffect(() => {
    const left = leftRef.current;
    const right = rightRef.current;
    if (!left || !right || typeof ResizeObserver === 'undefined') return;

    let frame = 0;
    // Resizing inside the callback would raise a ResizeObserver loop error.
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => alignRowHeights(left, right));
    });
    observer.observe(left);
    observer.observe(right);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [leftRef, rightRef]);
};
